// React-related imports
import { useContext, useEffect, useMemo, useState, useCallback } from 'react';

// Contexts
import { GeneralizationContext } from '../../contexts/GeneralizationContext';

// Hooks
import { useMappingSessionAPI } from '../../services/api';

// Components
import SvgIcon from '../../components/elements/SvgIcon';
import { Button, Dropdown } from '@GxT-Team/gxt-components';

// Types
import { MappingTypeEnum } from '../../types';
import {
  ListItem,
  ListItemWithNonParsedCatalogId,
  SourceVersion,
  UseSourceFilesParams,
} from './useSourceFiles.types';
import { getDropdownOptions } from '../../helpers/utils';
import { useFeatureFlag } from '../../contexts';
import { FeatureFlag } from '../../stores';
import { toNumber } from 'lodash';
import type { GeneralizationDocument } from '../../types';

/** Closed trigger: filename truncated, then "...", then " · vX.XX" (e.g. COA-0002 … Drug Substance Cert... · v1.00) */
const SOURCE_BUTTON_FILENAME_MAX_CHARS = 42;
const SOURCE_BUTTON_ELLIPSIS = '...';

const formatVersionSuffix = (version: GeneralizationDocument['version']) => {
  if (version === undefined || version === null) return '';
  if (typeof version === 'number' && Number.isFinite(version)) {
    return ` · v${version.toFixed(2)}`;
  }
  return ` · v${version}`;
};

const sourceDocumentLabels = (doc: Pick<GeneralizationDocument, 'filename' | 'version'>) => {
  const { filename, version } = doc;
  const name = filename ?? '';
  const versionSuffix = formatVersionSuffix(version);
  const listLabel = `${name}${versionSuffix}`;

  const namePart =
    name.length > SOURCE_BUTTON_FILENAME_MAX_CHARS
      ? `${name.slice(0, SOURCE_BUTTON_FILENAME_MAX_CHARS)}${SOURCE_BUTTON_ELLIPSIS}`
      : name;
  const buttonLabel = `${namePart}${versionSuffix}`;

  return { listLabel, buttonLabel };
};

export const useSourceFiles = ({
  handleLockedFileChange,
  sessionType = 'authoring',
}: UseSourceFilesParams) => {
  const {
    sourceDocuments,
    setSourceDocuments,
    sessionId,
    selectedCatalogId,
    setSelectedCatalogId,
    isParsed,
    handleSourcechange,
    lockSourceFile,
    mappingType,
  } = useContext(GeneralizationContext);

  const [catalogToRemove, setCatalogToRemove] = useState<string | null>(null);
  const [selectedDropdownValue, setSelectedDropdownValue] = useState<ListItem | null>(null);

  const currentFileVersion: SourceVersion = isParsed ? 'Parsed' : 'Original';

  const lockType = lockSourceFile ? 'lock' : 'unlock';

  const { removeSourceFiles } = useMappingSessionAPI();

  const handleRemoveSourceFile = () => {
    if (catalogToRemove && sessionId) {
      removeSourceFiles
        .mutateAsync({
          catalogIds: [parseInt(catalogToRemove)],
          sessionId,
        })
        .then(() => {
          const updatedSourceDocuments = new Map(sourceDocuments);
          updatedSourceDocuments.delete(catalogToRemove);
          setSourceDocuments(updatedSourceDocuments);
        })
        .finally(() => {
          setCatalogToRemove(null);
        });
    }
  };

  const handleRemoveFileIconClick = (id: string, event: React.MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
    setCatalogToRemove(id);
  };

  const handleFileRemoveModalClose = () => {
    setCatalogToRemove(null);
  };

  const [sourceDocsArray, _sourceDocsWithoutNonParsedCatalogId, sourcesHash, rowFullLabelByValue]: [
    ListItem[],
    ListItemWithNonParsedCatalogId[],
    Record<string, ListItem>,
    Map<string, string>,
  ] = useMemo(() => {
    const sourceDocumentArray: ListItem[] = [];
    const withNonParsedCatalogId: ListItemWithNonParsedCatalogId[] = [];
    const sourcesHash: Record<string, ListItem> = {};
    const rowFullLabelByValue = new Map<string, string>();

    if (sourceDocuments && selectedCatalogId) {
      const selectedCatalogInfo = sourceDocuments.get(selectedCatalogId.toString());

      Array.from(sourceDocuments.values()).forEach((item) => {
        const shouldPushSelectedItem =
          selectedCatalogId.toString() === item.id.toString() && !selectedCatalogInfo;
        const { listLabel, buttonLabel } = sourceDocumentLabels(item);
        const withoutNonParsedCatalogId: ListItem = {
          text: buttonLabel,
          value: shouldPushSelectedItem ? selectedCatalogId.toString() : item.id.toString(),
        };
        rowFullLabelByValue.set(withoutNonParsedCatalogId.value, listLabel);
        sourceDocumentArray.push(withoutNonParsedCatalogId);

        withNonParsedCatalogId.push({
          ...withoutNonParsedCatalogId,
          nonParsedCatalogId: item.nonParsedCatalogId?.toString() || '',
        });

        sourcesHash[item.id.toString()] = withoutNonParsedCatalogId;
        if (item.nonParsedCatalogId) {
          sourcesHash[item.nonParsedCatalogId.toString()] = withoutNonParsedCatalogId;
        }
      });
    }
    sourceDocumentArray.sort((a, b) =>
      (rowFullLabelByValue.get(a.value) ?? a.text).localeCompare(
        rowFullLabelByValue.get(b.value) ?? b.text,
      ),
    );
    return [sourceDocumentArray, withNonParsedCatalogId, sourcesHash, rowFullLabelByValue];
  }, [sourceDocuments, selectedCatalogId]);

  const handleSourceToggle = () => {
    if (selectedCatalogId && sourceDocuments) {
      const selectedDoc = sourceDocuments.get(selectedCatalogId.toString());
      if (selectedDoc) {
        handleSourcechange(selectedDoc.id.toString(), isParsed);
      } else {
        const parsedCatalogId = Array.from(sourceDocuments.values()).find(
          (value) => value.nonParsedCatalogId === selectedCatalogId,
        );
        handleSourcechange(parsedCatalogId?.id?.toString() || selectedCatalogId.toString(), false);
      }
    }
  };

  const removeButton = (id: string) =>
    mappingType === MappingTypeEnum.AUTHORING &&
    sessionType === MappingTypeEnum.AUTHORING && (
      <SvgIcon
        iconType="close"
        stroke="currentColor"
        className="w-4 flex-shrink-0 text-xs"
        data-testid={`remove-file-icon-${id}`}
        onClick={(event) => handleRemoveFileIconClick(id, event)}
      />
    );

  const { isEnabled } = useFeatureFlag();
  const handleDropdownSelect = (option: ListItem) => {
    const resolved = sourcesHash[option.value] ?? option;
    if (resolved.value === selectedDropdownValue?.value) return;

    setSelectedDropdownValue(resolved);
    if (isEnabled(FeatureFlag.AUTHORING_SMART_EDITOR)) {
      setSelectedCatalogId(toNumber(resolved.value));
    } else {
      handleSourcechange(resolved.value);
    }
  };

  useEffect(() => {
    const next = sourcesHash[selectedCatalogId.toString()];
    setSelectedDropdownValue((prev) => {
      if (prev?.value === next?.value && prev?.text === next?.text) return prev;
      return next;
    });
  }, [selectedCatalogId, sourcesHash]);

  const updateDropdownSelection = useCallback(
    (sourceId: string) => {
      const option = sourcesHash[sourceId];
      if (option) {
        setSelectedDropdownValue(option);
      }
    },
    [sourcesHash],
  );

  /** TODO: temp solution for source select comp and target store: refactor! */
  useEffect(() => {
    if (isEnabled(FeatureFlag.AUTHORING_SMART_EDITOR) && selectedCatalogId) {
      window.dispatchEvent(
        new CustomEvent('sourceFileSelected', {
          detail: { sourceId: selectedCatalogId, bypassDocumentLock: true },
        }),
      );
    }
  }, [selectedCatalogId, isEnabled]);

  const renderOption = (item: ListItem | null) => {
    if (!item) return null;

    return (
      <>
        <span
          className="cursor-pointer whitespace-normal break-words text-left"
          data-testid={`dropdown-item-${item.value}`}
          onClick={() => handleDropdownSelect(item)}>
          {rowFullLabelByValue.get(item.value) ?? item.text}
        </span>
        {selectedDropdownValue?.value !== item.value && <span>{removeButton(item.value)}</span>}
      </>
    );
  };

  const sourceFilesInitialValue = useMemo(
    () => ({
      value: selectedDropdownValue
        ? { text: selectedDropdownValue.text, value: selectedDropdownValue.value }
        : undefined,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedDropdownValue?.text, selectedDropdownValue?.value],
  );

  const sourceDropdownOptions = useMemo(
    () =>
      getDropdownOptions(
        sourceDocsArray.map(({ text, value }) => ({
          text,
          value,
        })),
      ),
    [sourceDocsArray],
  );

  const sourceFilesDropdown = (
    <div className="min-w-0 flex-1">
      <Dropdown
        key={selectedDropdownValue?.value ?? 'none'}
        type="select"
        options={sourceDropdownOptions}
        dropdownIcon
        disabled={sourceDocsArray.length === 0 || removeSourceFiles.isPending || lockSourceFile}
        initialValue={sourceFilesInitialValue}
        renderOption={(option) => renderOption(option?.value ?? null)}
        position="bottom"
        onSelect={(option) => option.value && handleDropdownSelect(option.value)}
        equalityFn={(a, b) => a?.value === b?.value}
        customStyles={{
          container: { height: '100%', width: '100%' },
          button: {
            width: '100%',
            minHeight: '2.5rem',
            height: '2.5rem',
            alignItems: 'center',
            overflow: 'hidden',
            paddingTop: '0.25rem',
            paddingBottom: '0.25rem',
          },
          placeholder: {
            textAlign: 'left',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            justifyContent: 'left',
            display: 'block',
            lineHeight: 1.35,
            minWidth: 0,
            flex: '1 1 0%',
            color: '#000',
          },
          itemsWrapper: { width: 'max(100%, min(42rem, 85vw))', maxHeight: '26rem' },
          item: {
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            whiteSpace: 'normal',
          },
        }}
      />
    </div>
  );

  const lockedFileSwitch = (
    <Button
      variant={lockType === 'lock' ? 'primary' : 'secondary'}
      onClick={handleLockedFileChange}
      disabled={!selectedCatalogId}>
      <div className="py-0.5">
        <SvgIcon iconType={lockType} />
      </div>
    </Button>
  );

  return {
    handleRemoveSourceFile,
    handleFileRemoveModalClose,
    sourceDocsArray,
    currentFileVersion,
    handleSourceToggle,
    sourceFilesDropdown,
    lockedFileSwitch,
    showSourceDeleteModal: !!catalogToRemove,
    updateDropdownSelection,
  };
};
